import { useState } from 'react'
import { Box, Button, Divider, Link, Paper, Typography } from '@mui/material'
import { ConfirmationModal } from 'components/utilities/confirmationModal'
import logo from '../../logo.png'
import { ReferralVerificationData } from './types'

export interface ReferralReviewViewProps {
  referral: ReferralVerificationData
  trackingRef: string
  initialChoice?: 'Yes' | 'No'
  onSubmit: (response: 'Yes' | 'No') => void | Promise<void>
}

export const ReferralReviewView = ({
  referral,
  trackingRef,
  initialChoice,
  onSubmit,
}: ReferralReviewViewProps): JSX.Element => {
  const [selected, setSelected] = useState<'Yes' | 'No' | undefined>(
    initialChoice
  )
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const showContactInfo = referral.requestType !== 'RESOURCE'
  const showResourceInfo = referral.requestType !== 'CONTACT'

  const handleChoose = (choice: 'Yes' | 'No'): void => {
    setSelected(choice)
    setShowConfirmModal(true)
  }

  const handleConfirm = (): void => {
    if (!selected) return
    onSubmit(selected)
  }

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        mb={2}
        gap={2}
      >
        <Box>
          <Typography variant="h6" color="primary.main" fontWeight={600}>
            Confirm Referral Information
          </Typography>
          <Typography variant="body1" color="text.disabled">
            Please confirm the referral information below for{' '}
            <Box component="span" color="text.primary" fontWeight={700}>
              {referral.center?.name || 'this resource'}
            </Box>
            .
          </Typography>
        </Box>
        <Box
          component="img"
          src={logo}
          alt="resolvexLogo"
          sx={{ width: { xs: 64, sm: 96 } }}
        />
      </Box>

      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="caption" color="text.secondary">
          Tracking Ref: {trackingRef}
        </Typography>
        <Divider sx={{ my: 2 }} />

        {showContactInfo && (
          <Box mb={showResourceInfo ? 3 : 0}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              Contact Information
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2">
              Name:{' '}
              {`${referral.firstName || ''} ${
                referral.lastName || ''
              }`.trim() || '-'}
            </Typography>
            <Typography variant="body2">
              Title/Role: {referral.titleRole || '-'}
            </Typography>
            <Typography variant="body2">
              Email: {referral.contactEmail || '-'}
            </Typography>
            <Typography variant="body2">
              Phone: {referral.contactPhone || '-'}
            </Typography>
          </Box>
        )}

        {showResourceInfo && referral.center && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              Resource Information
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2">
              Name: {referral.center.name}
            </Typography>
            <Typography variant="body2">
              Address: {referral.center.streetAddress}, {referral.center.city},{' '}
              {referral.center.state} {referral.center.zipCode}
            </Typography>
            <Typography variant="body2">
              Phone: {referral.center.phoneNumber}
            </Typography>
            <Typography variant="body2">
              Email: {referral.center.emailAddress}
            </Typography>
            <Typography variant="body2">
              Website: {referral.center.websiteAddress}
            </Typography>
            {!!referral.center.services?.length && (
              <Typography variant="body2">
                Services:{' '}
                {referral.center.services.map((s) => s.name).join(', ')}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {!selected && (
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
          >
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="large"
              onClick={() => handleChoose('No')}
            >
              No, information is incorrect
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              size="large"
              onClick={() => handleChoose('Yes')}
            >
              Yes, information is correct
            </Button>
          </Box>
        )}

        {selected && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Button
              fullWidth
              variant="contained"
              color={selected === 'Yes' ? 'success' : 'error'}
              size="large"
              onClick={() => setShowConfirmModal(true)}
            >
              {selected === 'Yes'
                ? 'Yes, information is correct'
                : 'No, information is incorrect'}
            </Button>
            <Link
              component="button"
              variant="body2"
              underline="hover"
              sx={{ mt: 1.5 }}
              onClick={() => setSelected(undefined)}
            >
              Change response
            </Link>
          </Box>
        )}
      </Paper>

      <ConfirmationModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        title="Confirm your response"
        color={selected === 'No' ? 'error' : 'success'}
        message={
          selected === 'Yes'
            ? 'Are you sure the information above is correct?'
            : 'Are you sure the information above is incorrect?'
        }
        callback={handleConfirm}
      />
    </Box>
  )
}
